import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";
import { useAuth } from "./AuthContext";

const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const { user } = useAuth();
  const [books, setBooks] = useState([]); // published (public site)
  const [adminBooks, setAdminBooks] = useState([]); // all (admin)
  const [loading, setLoading] = useState(true);

  const loadPublic = useCallback(async () => {
    try {
      const res = await api.get("/books");
      setBooks(res.data);
    } catch (e) {
      setBooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmin = useCallback(async () => {
    try {
      const res = await api.get("/admin/books");
      setAdminBooks(res.data);
    } catch (e) {
      setAdminBooks([]);
    }
  }, []);

  useEffect(() => {
    loadPublic();
  }, [loadPublic]);

  useEffect(() => {
    if (user?.role === "admin") loadAdmin();
  }, [user, loadAdmin]);

  const refresh = useCallback(async () => {
    await loadPublic();
    if (user?.role === "admin") await loadAdmin();
  }, [loadPublic, loadAdmin, user]);

  // Accepts a slug or an id: links carry the slug, but ids issued before
  // slugs existed still appear in bookmarks and shared URLs.
  const matches = (b, key) => b.id === key || b.slug === key;
  const getBook = (key) =>
    adminBooks.find((b) => matches(b, key)) || books.find((b) => matches(b, key));

  const fetchBook = async (id) => {
    try {
      const res = await api.get(`/books/${id}`);
      return res.data;
    } catch (e) {
      return null;
    }
  };

  const addBook = async (data) => {
    await api.post("/admin/books", data);
    await refresh();
  };
  const updateBook = async (id, data) => {
    await api.put(`/admin/books/${id}`, data);
    await refresh();
  };
  const deleteBook = async (id) => {
    await api.delete(`/admin/books/${id}`);
    await refresh();
  };
  const addChapter = async (bookId, data) => {
    await api.post(`/admin/books/${bookId}/chapters`, data);
    await refresh();
  };
  const updateChapter = async (bookId, chapterId, data) => {
    await api.put(`/admin/books/${bookId}/chapters/${chapterId}`, data);
    await refresh();
  };
  const deleteChapter = async (bookId, chapterId) => {
    await api.delete(`/admin/books/${bookId}/chapters/${chapterId}`);
    await refresh();
  };

  return (
    <CatalogContext.Provider
      value={{
        books,
        adminBooks,
        loading,
        getBook,
        fetchBook,
        addBook,
        updateBook,
        deleteBook,
        addChapter,
        updateChapter,
        deleteChapter,
        refresh,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
}

export const useCatalog = () => useContext(CatalogContext);
